import React, { useState, useContext } from 'react';
import axios from 'axios';
import config from '../config';
import { AuthContext } from '../components/Auth';
// Consider adding an icon library like react-icons if you want a proper copy icon
// import { FaCopy } from 'react-icons/fa'; 

const Settings: React.FC = () => {
    const { user, isAuthenticated, logout } = useContext(AuthContext);
    const [isClearing, setIsClearing] = useState(false);
    const [clearError, setClearError] = useState<string | null>(null);
    const [clearSuccess, setClearSuccess] = useState<string | null>(null);
    const [isRemovingDuplicates, setIsRemovingDuplicates] = useState(false);
    const [duplicateError, setDuplicateError] = useState<string | null>(null);
    const [duplicateSuccess, setDuplicateSuccess] = useState<string | null>(null);
    // State for categories section
    const [categoriesString, setCategoriesString] = useState('');
    const [isListingCategories, setIsListingCategories] = useState(false);
    const [listCategoriesError, setListCategoriesError] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);

    const handleClearDatabase = async () => {
        if (!window.confirm("Are you sure you want to clear leads, categories, and related data? This action cannot be undone!")) {
            return;
        }

        setIsClearing(true);
        setClearError(null);
        setClearSuccess(null);

        try {
            const response = await axios.post(`${config.apiBaseUrl}/settings/clear_database.php`, {});

            if (response.data.success) {
                setClearSuccess(response.data.message || 'Database cleared successfully.');
                // Optionally, trigger a refetch of data elsewhere if needed
                // queryClient.invalidateQueries(['leads']); 
            } else {
                setClearError(response.data.message || 'An error occurred while clearing the database.');
                if (response.data.errors) {
                     console.error('Detailed clearing errors:', response.data.errors);
                     setClearError(prev => `${prev} See console for details.`);
                }
            }
        } catch (err: any) {
            let errorMessage = 'Failed to send clear database request: ';
            if (err.response) {
                errorMessage += err.response.data?.message || err.response.statusText;
            } else if (err.request) {
                errorMessage += 'No response from server.';
            } else {
                errorMessage += err.message;
            }
            setClearError(errorMessage);
            console.error("Clear database API error:", err);
        } finally {
            setIsClearing(false);
        }
    };

    const handleLogout = () => {
        if (window.confirm("Are you sure you want to log out?")) {
            logout();
        }
    };

    const handleRemoveDuplicates = async () => {
        if (!window.confirm("Are you sure you want to remove duplicate leads? This will keep the newest record of each duplicate and remove older ones.")) {
            return;
        }

        setIsRemovingDuplicates(true);
        setDuplicateError(null);
        setDuplicateSuccess(null);

        try {
            const response = await axios.post(`${config.apiBaseUrl}/leads/remove_duplicates.php`, {});

            if (response.data.success) {
                setDuplicateSuccess(`Success! Found and removed ${response.data.duplicates_removed} duplicate lead(s).`);
                if (response.data.duplicates_removed === 0) {
                    setDuplicateSuccess('No duplicates were found in the database.');
                }
            } else {
                setDuplicateError(response.data.message || 'An error occurred while removing duplicates.');
                if (response.data.errors && response.data.errors.length > 0) {
                    console.error('Detailed errors:', response.data.errors);
                    setDuplicateError(prev => `${prev} See console for details.`);
                }
            }
        } catch (err: any) {
            let errorMessage = 'Failed to remove duplicates: ';
            if (err.response) {
                errorMessage += err.response.data?.message || err.response.statusText;
            } else if (err.request) {
                errorMessage += 'No response from server.';
            } else {
                errorMessage += err.message;
            }
            setDuplicateError(errorMessage);
            console.error("Remove duplicates API error:", err);
        } finally {
            setIsRemovingDuplicates(false);
        }
    };

    // --- New functions for Categories section ---
    const handleListCategories = async () => {
        setIsListingCategories(true);
        setListCategoriesError(null);
        setCategoriesString('');
        setCopySuccess(false);

        try {
            // Assume a new endpoint exists to fetch all category names
            const response = await axios.get(`${config.apiBaseUrl}/categories/list.php`);

            if (response.data.success && Array.isArray(response.data.categories)) {
                setCategoriesString(response.data.categories.join(', '));
            } else {
                setListCategoriesError(response.data.message || 'Failed to fetch categories.');
            }
        } catch (err: any) {
            let errorMessage = 'Failed to send list categories request: ';
             if (err.response) {
                errorMessage += err.response.data?.message || err.response.statusText;
            } else if (err.request) {
                errorMessage += 'No response from server.';
            } else {
                errorMessage += err.message;
            }
            setListCategoriesError(errorMessage);
            console.error("List categories API error:", err);
        } finally {
            setIsListingCategories(false);
        }
    };

    const handleCopyCategories = () => {
        if (!categoriesString) return;
        navigator.clipboard.writeText(categoriesString).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
        }).catch(err => {
            console.error('Failed to copy text: ', err);
            // Optionally, show an error message to the user
        });
    };
    // --- End of new functions ---

    return (
        <div className="p-4 md:p-6 space-y-6">
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

            {/* Authentication Section */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">Authentication</h2>
                
                <div className="mb-6">
                    <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                            {user?.picture ? (
                                <img 
                                    src={user.picture} 
                                    alt="Profile" 
                                    className="h-12 w-12 rounded-full" 
                                />
                            ) : (
                                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                                    <span className="text-blue-600 font-semibold text-lg">
                                        {user?.name?.charAt(0) || 'U'}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">
                                {user?.name || 'Authenticated User'}
                            </h3>
                            <p className="text-sm text-gray-500">
                                {user?.email || 'slav.durej@gmail.com'}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                                Status: {isAuthenticated ? 'Logged in' : 'Not logged in'}
                            </p>
                        </div>
                    </div>
                </div>
                
                <button 
                    onClick={handleLogout}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                    Log out
                </button>
            </div>

            {/* Duplicate Management Section */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">Database Management</h2>
                <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-800 mb-2">Remove Duplicate Leads</h3>
                    <p className="text-gray-600 mb-4">
                        This will scan your database for leads with identical business names and remove the duplicates, 
                        keeping only the most recent entry for each business.
                    </p>
                    
                    {duplicateSuccess && (
                        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
                            {duplicateSuccess}
                        </div>
                    )}
                    {duplicateError && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                            {duplicateError}
                        </div>
                    )}

                    <button 
                        onClick={handleRemoveDuplicates}
                        disabled={isRemovingDuplicates}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-wait"
                    >
                        {isRemovingDuplicates ? 'Processing...' : 'Remove Duplicate Leads'}
                    </button>
                </div>
            </div>

            {/* Categories Section - New */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">Industry Categories</h2>
                <div className="mb-4">
                    <p className="text-gray-600 mb-4">
                        Generate a comma-separated list of all industry categories currently in the database.
                    </p>
                    
                    {/* Container for the buttons */}
                    <div className="flex justify-between items-center mb-4">
                        <button
                            onClick={handleListCategories}
                            disabled={isListingCategories}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-wait"
                        >
                            {isListingCategories ? 'Loading...' : 'List Categories'}
                        </button>
                        
                        {/* Conditionally render copy button */}
                        {categoriesString && (
                             <button
                                onClick={handleCopyCategories}
                                className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300 disabled:opacity-50"
                                title="Copy to clipboard"
                                disabled={!categoriesString} 
                            >
                                {copySuccess ? 'Copied!' : 'Copy'}
                            </button>
                        )}
                    </div>

                     {listCategoriesError && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                            {listCategoriesError}
                        </div>
                    )}

                    {/* Textarea appears below buttons */}
                    {categoriesString && (
                        <div className="mt-2">
                             <textarea
                                readOnly
                                value={categoriesString}
                                className="w-full h-24 p-2 border border-gray-300 rounded-md bg-gray-50 resize-none"
                                placeholder="Categories will appear here..."
                            />
                        </div>
                    )}
                </div>
            </div>
            {/* End Categories Section */}

            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4 text-red-600">Danger Zone</h2>
                <p className="text-gray-600 mb-4">Clearing the database will permanently delete all leads, categories, statuses, email history, and related links. This is useful for starting fresh with new imports but cannot be undone.</p>
                
                {clearSuccess && (
                    <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
                        {clearSuccess}
                    </div>
                )}
                 {clearError && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                        {clearError}
                    </div>
                )}

                <button 
                    onClick={handleClearDatabase}
                    disabled={isClearing}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-wait"
                >
                    {isClearing ? 'Clearing...' : 'Clear Database Tables'}
                </button>
            </div>

            {/* Add more settings sections here later */}
        </div>
    );
};

export default Settings; 